import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  Heading,
  useToast,
  Container,
  Flex,
  Divider,
  HStack,
  Input,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import { getCashForecast } from "../service/ForecastService";

interface CashForecastingProps {
  onClose: () => void;
  onOpenAdvanced: () => void;
}

const CashForecasting: React.FC<CashForecastingProps> = ({
  onClose,
  onOpenAdvanced,
}) => {
  const toast = useToast();
  const chartRef = useRef<HTMLDivElement>(null);

  const [forecastData, setForecastData] = useState<any[]>([]);
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [forecastDate, setForecastDate] = useState<string>("");

  const fetchForecast = async () => {
    if (!forecastDate) {
      toast({ title: "Please enter a forecast date", status: "warning" });
      return;
    }

    try {
      const response = await getCashForecast(forecastDate);
      console.log(response);
      if (response?.forecast && response?.historical) {
        setForecastData(response.forecast);
        setHistoricalData(response.historical);
      } else {
        toast({ title: "Invalid response from server", status: "error" });
      }
    } catch (err) {
      toast({ title: "Failed to fetch forecast data", status: "error" });
    }
  };

  const getMergedChartData = () => {
    const merged: {
      date: string;
      netCashFlow: number | null;
      forecasted: number | null;
    }[] = [];

    // Format historical
    historicalData.forEach((item: any) => {
      merged.push({
        date: item.weekStartDate?.split("T")[0],
        netCashFlow: item.netCashFlow,
        forecasted: null,
      });
    });

// ✅ Insert bridge point if both data sets exist
  if (historicalData.length > 0 && forecastData.length > 0) {
    const lastHistorical = historicalData[historicalData.length - 1];
    const bridgeDate = lastHistorical.weekStartDate?.split("T")[0];
    const bridgeValue = lastHistorical.netCashFlow;

    merged.push({
      date: bridgeDate,
      netCashFlow: bridgeValue,
      forecasted: bridgeValue, // same value to "connect" lines
    });
  }


    // Format forecasted
    forecastData.forEach((item: any) => {
      merged.push({
        date: item.weekStartDate?.split("T")[0],
        netCashFlow: null,
        forecasted: item.forecastedNetCashFlow,
      });
    });

    return merged;
  };

  const downloadPDF = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF();
    pdf.addImage(imgData, "PNG", 10, 10, 180, 90);
    pdf.save("cash-forecast.pdf");
  };

  const handleDownloadJSON = () => {
    const blob = new Blob(
      [JSON.stringify([...historicalData, ...forecastData], null, 2)],
      {
        type: "application/json",
      }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cashForecast.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = () => {
    const merged = [...historicalData, ...forecastData];
    if (!merged.length) return;

    const keys = Object.keys(merged[0]);
    const csvRows = [
      keys.join(","),
      ...merged.map((row: any) =>
        keys
          .map((k) => {
            let value = row[k];
            if (
              typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
            ) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cashForecast.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container maxW="4xl" py={6}>
      <Heading size="lg" mb={4}>
        💰 Cash Forecast
      </Heading>

      <Flex wrap="wrap" gap={4} mb={6} align="center">
        <Input
          type="date"
          value={forecastDate}
          onChange={(e) => setForecastDate(e.target.value)}
          width="250px"
        />
        <Button colorScheme="blue" onClick={fetchForecast}>
          🔍 Get Forecast
        </Button>
        {/* <Button colorScheme="green" onClick={downloadPDF}>
          📄 Download PDF
        </Button> */}
        <Text>
          Enter a future date to forecast net cash flow. Trains using past N
          weeks based on date.
        </Text>
      </Flex>

      <Divider mb={4} />

      {forecastData.length > 0 && historicalData.length > 0 && (
        <Box
          ref={chartRef}
          p={4}
          borderWidth={1}
          borderRadius="lg"
          bg="white"
          shadow="md"
          mt={6}
        >
          {/* <Text fontWeight="bold" mb={2}>
            📈 Net Cash Flow: Historical + Forecast
          </Text> */}
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={getMergedChartData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} >
               <Label value="Date" position="insideBottom"/>
               </XAxis>
              {/* <YAxis /> */}
            <YAxis>
                <Label angle={-90} value="Cash" position="insideLeft" />
                </YAxis>
              <Tooltip />
              {/* <Legend /> */}
 <Legend 
                layout="horizontal"
                verticalAlign="top"
                align="right"
              />
              <Line
                type="monotone"
                dataKey="netCashFlow"
                stroke="#3182CE"
                name="Historical Net Cash Flow"
                dot={{ r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="forecasted"
                stroke="#2F855A"
                strokeDasharray="5 5"
                name="Forecasted Net Cash Flow"
                dot={{ r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {(forecastData.length > 0 || historicalData.length > 0) && (
        <Box mt={4} p={4} borderWidth={1} borderRadius="md" bg="gray.50">
          {/* <Text fontWeight="bold" mb={2}>
            Download Data
          </Text> */}
          <HStack spacing={4}>
            <Button size="sm" colorScheme="green" onClick={handleDownloadCSV}>
              Download CSV
            </Button>
            <Button size="sm" colorScheme="orange" onClick={handleDownloadJSON}>
              Download JSON
            </Button>
             <Button size ="sm" colorScheme="green" onClick={downloadPDF}>
            Download PDF
        </Button>
          </HStack>
        </Box>
      )}

      <Box mt={4}>
        <Button
          size="sm"
          variant="link"
          colorScheme="blue"
          onClick={onOpenAdvanced}
        >
          ← Back to Forecast Selection
        </Button>
      </Box>
    </Container>
  );
};

export default CashForecasting;
